import type { RecordingResult } from "./types";

const WARNING_BYTES = 500 * 1024 * 1024;
const HARD_LIMIT_BYTES = 1024 * 1024 * 1024;

export interface RecorderCallbacks {
  onProgress?: (seconds: number, bytes: number) => void;
  onWarning?: (bytes: number) => void;
  onStopped?: (result: RecordingResult) => void;
  onError?: (error: Error) => void;
}

function chooseMimeType(): string {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
  ];
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? "";
}

export class CanvasRecorder {
  #canvas: HTMLCanvasElement;
  #audioStream: () => MediaStream | undefined;
  #callbacks: RecorderCallbacks;
  #recorder?: MediaRecorder;
  #stream?: MediaStream;
  #chunks: Blob[] = [];
  #bytes = 0;
  #startedAt = 0;
  #warned = false;
  #timer?: number;
  #stopPromise?: Promise<RecordingResult>;
  #resolveStop?: (result: RecordingResult) => void;
  #rejectStop?: (error: Error) => void;

  constructor(canvas: HTMLCanvasElement, audioStream: () => MediaStream | undefined, callbacks: RecorderCallbacks = {}) {
    this.#canvas = canvas;
    this.#audioStream = audioStream;
    this.#callbacks = callbacks;
  }

  get active(): boolean {
    return this.#recorder?.state === "recording" || this.#recorder?.state === "paused";
  }

  get bytes(): number {
    return this.#bytes;
  }

  start(frameRate = 60): void {
    if (this.active) throw new Error("A CRTVAR recording is already in progress");
    if (typeof MediaRecorder === "undefined" || typeof this.#canvas.captureStream !== "function") {
      throw new Error("Canvas recording is not supported by this browser");
    }
    const stream = this.#canvas.captureStream(frameRate);
    for (const track of this.#audioStream()?.getAudioTracks() ?? []) stream.addTrack(track.clone());
    const mimeType = chooseMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    this.#chunks = [];
    this.#bytes = 0;
    this.#warned = false;
    this.#startedAt = performance.now();
    this.#stream = stream;
    this.#recorder = recorder;
    this.#stopPromise = new Promise<RecordingResult>((resolve, reject) => {
      this.#resolveStop = resolve;
      this.#rejectStop = reject;
    });
    recorder.addEventListener("dataavailable", (event) => this.#handleData(event));
    recorder.addEventListener("error", (event) => {
      const error = event.error || new Error("MediaRecorder failed");
      this.#callbacks.onError?.(error);
      this.#rejectStop?.(error);
      this.#cleanup();
    });
    recorder.addEventListener("stop", () => this.#finish());
    recorder.start(1000);
    this.#timer = window.setInterval(() => {
      this.#callbacks.onProgress?.((performance.now() - this.#startedAt) / 1000, this.#bytes);
    }, 1000);
  }

  #handleData(event: BlobEvent): void {
    if (!event.data.size) return;
    this.#chunks.push(event.data);
    this.#bytes += event.data.size;
    if (!this.#warned && this.#bytes >= WARNING_BYTES) {
      this.#warned = true;
      this.#callbacks.onWarning?.(this.#bytes);
    }
    if (this.#bytes >= HARD_LIMIT_BYTES && this.#recorder?.state !== "inactive") this.#recorder?.stop();
  }

  stop(): Promise<RecordingResult> {
    if (!this.#recorder || !this.#stopPromise) return Promise.reject(new Error("No CRTVAR recording is active"));
    if (this.#recorder.state !== "inactive") this.#recorder.stop();
    return this.#stopPromise;
  }

  #finish(): void {
    const recorder = this.#recorder;
    if (!recorder) return;
    const mimeType = recorder.mimeType || this.#chunks[0]?.type || "video/webm";
    const blob = new Blob(this.#chunks, { type: mimeType });
    const result: RecordingResult = {
      blob,
      mimeType,
      bytes: blob.size,
      durationMs: performance.now() - this.#startedAt,
    };
    this.#resolveStop?.(result);
    this.#callbacks.onStopped?.(result);
    this.#cleanup();
  }

  #cleanup(): void {
    if (this.#timer !== undefined) window.clearInterval(this.#timer);
    this.#timer = undefined;
    this.#stream?.getTracks().forEach((track) => track.stop());
    this.#stream = undefined;
    this.#recorder = undefined;
    this.#resolveStop = undefined;
    this.#rejectStop = undefined;
    this.#stopPromise = undefined;
  }

  dispose(): void {
    if (this.#recorder?.state !== "inactive") this.#recorder?.stop();
    else this.#cleanup();
  }
}

